import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import { useUpdateVehicle } from '@/hooks/useVehicles';
import { useNotify } from '@/hooks/useNotify';
import {
  overlayHasKey,
  validateVehicleFields,
  type VehicleFieldsData,
} from './VehicleFormFields';
import { notifyVehicleSaveError } from './vehicleSaveError';
import { CopyButton } from '@/components/common/CopyButton';

export interface InlineVehicleFieldsVehicle {
  id: string;
  registrationNumber: string;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  vin?: string | null;
  notes?: string | null;
}

export interface InlineVehicleOverlay {
  traficom: Record<string, unknown> | null | undefined;
  overrides: Record<string, unknown> | null | undefined;
}

interface InlineVehicleFieldsProps {
  vehicle: InlineVehicleFieldsVehicle;
  readOnly?: boolean;
  overlay?: InlineVehicleOverlay;
  /**
   * Fired after a successful save with the saved values. Callers that hold
   * their own copy of the vehicle (the work-order create wizard) use it so a
   * corrected plate does not revert when this component remounts (#2883).
   */
  onSaved?: (vehicle: InlineVehicleFieldsVehicle) => void;
}

const VIEW_FIELDS = ['registrationNumber', 'make', 'model', 'year'] as const;
const VIEW_FIELDS_WITH_VIN = [...VIEW_FIELDS, 'vin'] as const;

const IDENTITY_OVERLAY_KEYS: Record<'make' | 'model' | 'year' | 'vin', string> = {
  make: 'make',
  model: 'model',
  year: 'modelYear',
  vin: 'vin',
};

function yearToInput(year: number | null | undefined): string {
  if (year == null || year === 0) return '';
  return String(year);
}

function fieldsFrom(vehicle: InlineVehicleFieldsVehicle): VehicleFieldsData {
  return {
    registrationNumber: vehicle.registrationNumber,
    make: vehicle.make ?? '',
    model: vehicle.model ?? '',
    year: yearToInput(vehicle.year),
    vin: vehicle.vin ?? '',
    mileage: '',
  };
}

function displayFrom(vehicle: InlineVehicleFieldsVehicle): InlineVehicleFieldsVehicle {
  const next: InlineVehicleFieldsVehicle = {
    id: vehicle.id,
    registrationNumber: vehicle.registrationNumber,
    make: vehicle.make ?? null,
    model: vehicle.model ?? null,
    year: vehicle.year ?? null,
  };
  if ('vin' in vehicle) {
    next.vin = vehicle.vin ?? null;
  }
  if ('notes' in vehicle) {
    next.notes = vehicle.notes ?? null;
  }
  return next;
}

function viewValue(
  data: VehicleFieldsData,
  field: (typeof VIEW_FIELDS_WITH_VIN)[number],
): string {
  const value = data[field].trim();
  return value === '' ? '—' : value;
}

function valuesMatchRegistry(value: unknown, registry: unknown): boolean {
  if (value == null || value === '') {
    return registry == null || registry === '';
  }
  return String(value) === String(registry);
}

function buildIdentityOverrides(
  data: VehicleFieldsData,
  includeVin: boolean,
  existing: Record<string, unknown> | null | undefined,
  traficom: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  const next: Record<string, unknown> = { ...(existing ?? {}) };
  const pairs: Array<{ key: string; value: unknown; registry: unknown }> = [
    { key: 'make', value: data.make.trim() || null, registry: traficom?.['make'] },
    { key: 'model', value: data.model.trim() || null, registry: traficom?.['model'] },
    {
      key: 'modelYear',
      value: data.year ? Number(data.year) : null,
      registry: traficom?.['modelYear'],
    },
  ];
  if (includeVin) {
    pairs.push({ key: 'vin', value: data.vin.trim() || null, registry: traficom?.['vin'] });
  }
  for (const pair of pairs) {
    if (valuesMatchRegistry(pair.value, pair.registry)) {
      delete next[pair.key];
    } else {
      next[pair.key] = pair.value;
    }
  }
  return Object.keys(next).length === 0 ? null : next;
}

function buildUpdatePayload(
  data: VehicleFieldsData,
  includeVin: boolean,
  notes: string | undefined,
  overlay: InlineVehicleOverlay | undefined,
): {
  registrationNumber: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  notes?: string | null;
  manualOverrides?: Record<string, unknown> | null;
} {
  const payload: {
    registrationNumber: string;
    make?: string;
    model?: string;
    year?: number;
    vin?: string;
    notes?: string | null;
    manualOverrides?: Record<string, unknown> | null;
  } = {
    registrationNumber: data.registrationNumber.trim(),
  };

  const make = data.make.trim();
  if (make) payload.make = make;

  const model = data.model.trim();
  if (model) payload.model = model;

  if (data.year) {
    payload.year = Number(data.year);
  }

  if (includeVin) {
    const vin = data.vin.trim();
    if (vin) payload.vin = vin;
  }

  if (notes !== undefined) {
    const trimmed = notes.trim();
    payload.notes = trimmed === '' ? null : trimmed;
  }

  if (overlay) {
    payload.manualOverrides = buildIdentityOverrides(
      data,
      includeVin,
      overlay.overrides,
      overlay.traficom,
    );
  }

  return payload;
}

function FieldRow({
  label,
  value,
  sourceLabel,
  onRestore,
  restoreLabel,
  copyLabel,
}: {
  label: string;
  value: string;
  sourceLabel?: string;
  onRestore?: () => void;
  restoreLabel?: string;
  /** Shows a copy button for the value (#3383); hidden for the empty dash. */
  copyLabel?: string;
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 2, alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        {sourceLabel && (
          <Chip label={sourceLabel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        <Typography variant="body2" fontWeight={500} sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
          {value}
        </Typography>
        {copyLabel && value !== '—' ? <CopyButton value={value} label={copyLabel} /> : null}
        {onRestore && restoreLabel && (
          <Tooltip title={restoreLabel}>
            <IconButton size="small" onClick={onRestore} aria-label={restoreLabel}>
              <UndoIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export function InlineVehicleFields({
  vehicle,
  readOnly = false,
  overlay,
  onSaved,
}: InlineVehicleFieldsProps) {
  const { t } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateMutation = useUpdateVehicle();

  const [display, setDisplay] = useState<InlineVehicleFieldsVehicle>(() => displayFrom(vehicle));
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<VehicleFieldsData>(() => fieldsFrom(vehicle));
  const [notesDraft, setNotesDraft] = useState(() => vehicle.notes ?? '');
  const [errors, setErrors] = useState<Partial<Record<keyof VehicleFieldsData, string>>>({});
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const vehicleRef = useRef(vehicle);
  vehicleRef.current = vehicle;

  useEffect(() => {
    const current = vehicleRef.current;
    setDisplay(displayFrom(current));
    if (!editingRef.current) {
      setFormData(fieldsFrom(current));
      setNotesDraft(current.notes ?? '');
      setErrors({});
    }
  }, [
    vehicle.id,
    vehicle.registrationNumber,
    vehicle.make,
    vehicle.model,
    vehicle.year,
    vehicle.vin,
    vehicle.notes,
  ]);

  const viewData = fieldsFrom(display);
  const showVin = 'vin' in vehicle;
  const showNotes = 'notes' in vehicle;
  const viewFields = showVin ? VIEW_FIELDS_WITH_VIN : VIEW_FIELDS;

  const handleFieldChange = (field: keyof VehicleFieldsData, value: string) => {
    const nextValue = field === 'registrationNumber' ? value.toUpperCase() : value;
    setFormData((prev) => ({ ...prev, [field]: nextValue }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const startEditing = () => {
    setFormData(fieldsFrom(display));
    setNotesDraft(display.notes ?? '');
    setErrors({});
    setEditing(true);
  };

  const handleCancel = () => {
    setFormData(fieldsFrom(display));
    setNotesDraft(display.notes ?? '');
    setErrors({});
    setEditing(false);
  };

  const handleSave = async () => {
    const nextErrors = validateVehicleFields(formData, t);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const payload = buildUpdatePayload(formData, showVin, showNotes ? notesDraft : undefined, overlay);
    const reg = payload.registrationNumber;

    try {
      await updateMutation.mutateAsync({
        id: display.id,
        data: payload,
      });
      const saved: InlineVehicleFieldsVehicle = {
        id: display.id,
        registrationNumber: payload.registrationNumber,
        make: payload.make ?? null,
        model: payload.model ?? null,
        year: payload.year ?? null,
        ...(showVin ? { vin: payload.vin ?? null } : {}),
        ...(showNotes ? { notes: payload.notes ?? null } : {}),
      };
      setDisplay(saved);
      setEditing(false);
      onSaved?.(saved);
      notify.success(t('form.updated'), t('form.updatedDesc', { reg }));
    } catch (err) {
      notifyVehicleSaveError(err, notify, t, tc, { registrationNumber: reg });
    }
  };

  const handleRestore = async (field: 'make' | 'model' | 'year' | 'vin') => {
    if (!overlay) return;
    const overlayKey = IDENTITY_OVERLAY_KEYS[field];
    const registry = overlay.traficom?.[overlayKey];
    const nextOverrides: Record<string, unknown> = { ...(overlay.overrides ?? {}) };
    delete nextOverrides[overlayKey];

    const data: {
      make?: string;
      model?: string;
      year?: number;
      vin?: string;
      manualOverrides: Record<string, unknown> | null;
    } = {
      manualOverrides: Object.keys(nextOverrides).length === 0 ? null : nextOverrides,
    };
    if (field === 'make' && typeof registry === 'string') data.make = registry;
    if (field === 'model' && typeof registry === 'string') data.model = registry;
    if (field === 'year' && typeof registry === 'number') data.year = registry;
    if (field === 'vin' && typeof registry === 'string') data.vin = registry;

    try {
      await updateMutation.mutateAsync({ id: display.id, data });
      setDisplay((prev) => ({
        ...prev,
        ...(field === 'make' ? { make: typeof registry === 'string' ? registry : null } : {}),
        ...(field === 'model' ? { model: typeof registry === 'string' ? registry : null } : {}),
        ...(field === 'year' ? { year: typeof registry === 'number' ? registry : null } : {}),
        ...(field === 'vin' ? { vin: typeof registry === 'string' ? registry : null } : {}),
      }));
      notify.success(t('form.updated'), t('source.restoredDesc'));
    } catch (err) {
      notifyVehicleSaveError(err, notify, t, tc);
    }
  };

  const sourceFor = (field: (typeof VIEW_FIELDS_WITH_VIN)[number]): string | undefined => {
    if (!overlay || field === 'registrationNumber') return undefined;
    const overlayKey = IDENTITY_OVERLAY_KEYS[field];
    if (overlayHasKey(overlay.overrides, overlayKey)) return t('source.manual');
    if (overlay.traficom?.[overlayKey] != null && overlay.traficom[overlayKey] !== '') {
      return t('source.registry');
    }
    return undefined;
  };

  const canRestore = (field: (typeof VIEW_FIELDS_WITH_VIN)[number]): boolean => {
    if (readOnly || !overlay || field === 'registrationNumber') return false;
    return overlayHasKey(overlay.overrides, IDENTITY_OVERLAY_KEYS[field]);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <Stack spacing={2}>
              <TextField
                label={t('registrationNumber')}
                value={formData.registrationNumber}
                onChange={(e) => { handleFieldChange('registrationNumber', e.target.value); }}
                error={!!errors.registrationNumber}
                helperText={errors.registrationNumber ?? t('registrationNumberHint')}
                fullWidth
                required
                disabled={updateMutation.isPending}
              />
              <TextField
                label={t('make')}
                value={formData.make}
                onChange={(e) => { handleFieldChange('make', e.target.value); }}
                fullWidth
                disabled={updateMutation.isPending}
              />
              <TextField
                label={t('model')}
                value={formData.model}
                onChange={(e) => { handleFieldChange('model', e.target.value); }}
                fullWidth
                disabled={updateMutation.isPending}
              />
              <TextField
                label={t('year')}
                value={formData.year}
                onChange={(e) => { handleFieldChange('year', e.target.value); }}
                error={!!errors.year}
                helperText={errors.year}
                fullWidth
                type="number"
                disabled={updateMutation.isPending}
              />
              {showVin && (
                <TextField
                  label={t('vin')}
                  value={formData.vin}
                  onChange={(e) => { handleFieldChange('vin', e.target.value); }}
                  error={!!errors.vin}
                  helperText={errors.vin ?? t('vinHint')}
                  fullWidth
                  disabled={updateMutation.isPending}
                />
              )}
              {showNotes && (
                <TextField
                  label={t('specialNotes')}
                  value={notesDraft}
                  onChange={(e) => { setNotesDraft(e.target.value); }}
                  helperText={t('specialNotesHint')}
                  fullWidth
                  multiline
                  minRows={3}
                  slotProps={{ htmlInput: { maxLength: 2000 } }}
                  disabled={updateMutation.isPending}
                />
              )}
            </Stack>
          ) : (
            <>
              {viewFields.map((field) => (
                <FieldRow
                  key={field}
                  label={t(field)}
                  value={viewValue(viewData, field)}
                  {...(field === 'vin' ? { copyLabel: tc('copyField', { field: t('vin') }) } : {})}
                  {...(sourceFor(field) !== undefined ? { sourceLabel: sourceFor(field) } : {})}
                  {...(canRestore(field) && field !== 'registrationNumber'
                    ? {
                        onRestore: () => {
                          void handleRestore(field);
                        },
                        restoreLabel: t('source.restore'),
                      }
                    : {})}
                />
              ))}
              {showNotes && (
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('specialNotes')}
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ whiteSpace: 'pre-wrap' }}>
                    {(display.notes ?? '').trim() === '' ? '—' : display.notes}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
        {!editing && !readOnly && (
          <Tooltip title={tc('edit')}>
            <IconButton
              size="small"
              onClick={startEditing}
              aria-label={tc('edit')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {editing && (
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button onClick={handleCancel} disabled={updateMutation.isPending}>
            {tc('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => { void handleSave(); }}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? tc('saving') : tc('save')}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
